import { parseISO, format, type Locale } from "date-fns";
import { enUS, nb, sv, da, de, fr, es, enGB } from "date-fns/locale";

const LOCALE_MAP: Record<string, Locale> = {
  NO: nb,
  NOR: nb,
  SE: sv,
  SWE: sv,
  DK: da,
  DNK: da,
  DE: de,
  DEU: de,
  FR: fr,
  FRA: fr,
  ES: es,
  ESP: es,
  GB: enGB,
  GBR: enGB,
  UK: enGB,
  US: enUS,
  USA: enUS,
};

const getLocale = (countryCode: string): Locale => {
  return LOCALE_MAP[countryCode.toUpperCase()] ?? enUS;
};

/**
 * Extracts the local date/time portion from an ISO 8601 string,
 * discarding timezone information.
 *
 * Example: "2025-09-30T07:17:19-07:00" → "2025-09-30T07:17:19"
 *
 * This preserves the merchant's local time as it appeared on the receipt,
 * rather than converting to the viewer's timezone.
 */
const extractLocalDateTime = (isoString: string): string => {
  const match = isoString.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})/);
  return match?.[1] ?? "";
};

/**
 * Formats a date string in the merchant's local timezone with locale-specific formatting.
 *
 * @param dateString - ISO 8601 date string with timezone offset (e.g., "2025-09-30T07:17:19-07:00")
 * @param countryCode - Country code for locale selection (e.g., "NO", "US", "SE")
 * @param includeSeconds - Whether to include seconds in the time display
 * @returns Formatted date string in the merchant's local time, or empty string if invalid
 */
export const formatMerchantLocalDate = (
  dateString: string | undefined,
  countryCode: string,
  includeSeconds: boolean = true
): string => {
  if (!dateString) return "";

  const localDateTime = extractLocalDateTime(dateString);
  if (!localDateTime) return "";

  const date = parseISO(localDateTime);
  if (isNaN(date.getTime())) return "";

  const locale = getLocale(countryCode);

  // 'P' = localized date, 'p' = localized time, 'pp' = localized time with seconds
  const formatString = includeSeconds ? "P pp" : "P p";

  return format(date, formatString, { locale });
};

/**
 * Formats a date for use in filenames (replaces slashes with dashes).
 */
export const formatDateForFilename = (
  dateString: string,
  countryCode: string
): string => {
  const localDateTime = extractLocalDateTime(dateString);
  if (!localDateTime) return "";

  const date = parseISO(localDateTime);
  if (isNaN(date.getTime())) return "";

  const locale = getLocale(countryCode);

  return format(date, "P", { locale }).replace(/\//g, "-");
};
